import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable } from "rxjs";

import { FeatureDataStateService } from "./feature-data-state.service";

type EditedDataState = string | Record<string, unknown>;
type StatusState = boolean | string;

/**
 * InputErrorsStateService
 * --------------------------------------------------------------------
 * Owns the "edited data + validity + input-error" streams for the
 * features that do not fit cleanly into `FeatureDataStateService`:
 *
 * - TouchDim
 * - diia253-maintenance
 * - DC-emergency
 * - Dali102 / Dali102and207 / Aux / Lsi / AocMultiChannel input-error streams
 *
 * The `getdiia253maintenanceEditedData` observable, for legacy
 * compatibility, points to the diia253 data stream (not the editedData
 * subject).
 *
 * Public names mirror the legacy `ApiService` API.
 */
@Injectable({ providedIn: "root" })
export class InputErrorsStateService {
  // --- DC-emergency ------------------------------------------------
  readonly dcemergencyEditedData = new BehaviorSubject<EditedDataState>("");
  readonly getDcemergencyEditedData: Observable<EditedDataState> =
    this.dcemergencyEditedData.asObservable();

  readonly isDcemergencyValueValid = new BehaviorSubject<StatusState>("");
  readonly setDcemergencyDefaultData = new BehaviorSubject<StatusState>("");
  readonly getDcemergencyDefaultData: Observable<StatusState> =
    this.setDcemergencyDefaultData.asObservable();
  readonly setDcemergencyInputError = new BehaviorSubject<boolean>(false);
  readonly getDcemergencyInputError: Observable<boolean> =
    this.setDcemergencyInputError.asObservable();

  // --- TouchDim ----------------------------------------------------
  readonly touchDimEditedData = new BehaviorSubject<EditedDataState>("");
  readonly getTouchDimEditedData: Observable<EditedDataState> = this.touchDimEditedData.asObservable();

  readonly isTouchDimValueValid = new BehaviorSubject<StatusState>("");
  readonly setTouchDimDefaultData = new BehaviorSubject<StatusState>("");
  readonly getTouchDimDefaultData: Observable<StatusState> = this.setTouchDimDefaultData.asObservable();
  readonly setTouchDimInputError = new BehaviorSubject<boolean>(false);
  readonly getTouchDimInputError: Observable<boolean> = this.setTouchDimInputError.asObservable();
  readonly setTouchDimValues = new BehaviorSubject<EditedDataState>("");

  // --- diia253 -----------------------------------------------------
  readonly diia253maintenanceEditedData = new BehaviorSubject<EditedDataState>("");
  /**
   * Legacy alias: `getdiia253maintenanceEditedData` intentionally
   * returned the `diia253maintenancedata` stream (not the editedData
   * subject) before the refactor.
   */
  readonly getdiia253maintenanceEditedData: Observable<string | boolean | number | Record<string, unknown> | unknown[]>;
  readonly setdiia253maintenanceInputError = new BehaviorSubject<boolean>(false);
  readonly getdiia253maintenanceInputError: Observable<boolean> =
    this.setdiia253maintenanceInputError.asObservable();

  // --- Cross-feature input-error streams (no matching defaults) ----
  readonly setDali102AddressingInputError = new BehaviorSubject<boolean>(false);
  readonly getDali102AddressingInputError: Observable<boolean> =
    this.setDali102AddressingInputError.asObservable();

  readonly setDali102and207ConfigurationInputError = new BehaviorSubject<boolean>(false);
  readonly getDali102and207ConfigurationInputError: Observable<boolean> =
    this.setDali102and207ConfigurationInputError.asObservable();

  readonly setAuxInputError = new BehaviorSubject<boolean>(false);
  readonly getAuxInputError: Observable<boolean> = this.setAuxInputError.asObservable();

  readonly setLsiInputError = new BehaviorSubject<boolean>(false);
  readonly getLsiInputError: Observable<boolean> = this.setLsiInputError.asObservable();

  readonly setAocMultiChannelInputError = new BehaviorSubject<boolean>(false);
  readonly getAocMultiChannelInputError: Observable<boolean> =
    this.setAocMultiChannelInputError.asObservable();

  readonly setOtpInputError = new BehaviorSubject<boolean>(false);
  readonly getOtpInputError: Observable<boolean> = this.setOtpInputError.asObservable();

  readonly setDscInputError = new BehaviorSubject<boolean>(false);
  readonly getDscInputError: Observable<boolean> = this.setDscInputError.asObservable();

  readonly setLowMainsLockInputError = new BehaviorSubject<boolean>(false);
  readonly getLowMainsLockInputError: Observable<boolean> = this.setLowMainsLockInputError.asObservable();

  readonly setDipswitchInputError = new BehaviorSubject<boolean>(false);
  readonly getDipswitchInputError: Observable<boolean> = this.setDipswitchInputError.asObservable();

  constructor(private featureData: FeatureDataStateService) {
    this.getdiia253maintenanceEditedData =
      this.featureData.diia253maintenancedata.asObservable();
  }

  // --- Setters -----------------------------------------------------
  setDcemergencyEditedData(value: EditedDataState): void {
    this.dcemergencyEditedData.next(value);
  }
  sendDcemergencyValidData(data: StatusState): void {
    this.isDcemergencyValueValid.next(data);
  }
  sendDcemergencyDefaultData(data: StatusState): void {
    this.setDcemergencyDefaultData.next(data);
  }
  sendDcemergencyInputError(data: boolean): void {
    this.setDcemergencyInputError.next(data);
  }

  setTouchDimEditedData(value: EditedDataState): void {
    this.touchDimEditedData.next(value);
  }
  sendTouchDimValidData(data: StatusState): void {
    this.isTouchDimValueValid.next(data);
  }
  sendTouchDimDefaultData(data: StatusState): void {
    this.setTouchDimDefaultData.next(data);
  }
  sendTouchDimInputError(data: boolean): void {
    this.setTouchDimInputError.next(data);
  }
  sendTouchDimObj(data: EditedDataState): void {
    this.setTouchDimValues.next(data);
  }

  setdiia253maintenanceEditedData(value: EditedDataState): void {
    this.diia253maintenanceEditedData.next(value);
  }
  senddiia253maintenanceInputError(data: boolean): void {
    this.setdiia253maintenanceInputError.next(data);
  }

  sendDali102AddressingInputError(data: boolean): void {
    this.setDali102AddressingInputError.next(data);
  }
  sendDali102and207ConfigurationInputError(data: boolean): void {
    this.setDali102and207ConfigurationInputError.next(data);
  }
  sendAocMultiChannelInputError(data: boolean): void {
    this.setAocMultiChannelInputError.next(data);
  }
}
